/**
 * rejection-alert.js
 */

import React from 'react';
import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Alert } from '@mui/material';
import { Types } from 'utils/constants';
import { UserContext } from 'contexts/user/user-context';


const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(3),
    '& .section': {
      display: 'flex',
      marginBottom: 8
    }
  },
  comments: {
    display: 'flex',
    margin: `0px ${theme.spacing(4)}`,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontSize: theme.typography.sizes[2],
    whiteSpace: 'pre-line',
  },
  instructions: {
    marginTop: theme.spacing(2)
  }
}));


export default function RejectionAlert(props) {
  const {
    review,
    ...rest
  } = props;
  const classes = useStyles();
  const { user } = React.useContext(UserContext);


  // NOTES:
  // this is pretty messy and i strongly dislike the logic we are using to determine the previous review state
  // which is deemed necessary to show to supervisors. we should be tracking the previous review state in the 
  // DB because that would make the logic explicit and clear instead of what i've done below which i feel
  // the need to explain.

  // HANDLERS/TRAINERS:
  // the logic is fairly straight-forward. we only show the alert if the current review state is REJECTED.
  // the rejection reason is shown so the handler can update their records according to the feedback 
  // provided in the rejection reason.

  // SUPERVISORS:
  // if the current review state is NOT REVIEWED or REJECTED and we have a review.reviewer this implies
  // that there is a previous review state which should inform the supervisor. to determine the previous
  // review state we use the existence of review.rejectionReason. if there is no reason then we assume
  // the previous state was ACCEPTED and that the handler has made changes which require another review.
  // if review.rejectionReason exists then we assume the previous state was REJECTED.


  if (
    // if handler/trainer and not rejected -> show nothing
    ((user.role.isHandler || user.role.isTrainer) && review?.status !== Types.ReviewStatus.REJECTED)
    // if supervisor and no review user -> show nothing
    || (user.role.isSupervisor && (review?.status === Types.ReviewStatus.ACCEPTED || !review?.reviewer))
  ) {
    return null;
  }


  const title = () => {
    // HANDLER / TRAINER
    if (user.role.isHandler || user.role.isTrainer) {
      return review.rejectedReason
        ? `Supervisor ${review.reviewer.name} has requested the following changes:`
        : `Supervisor ${review.reviewer.name} has rejected this record.`
    }
    // SUPERVISOR 
    else if (user.role.isSupervisor) {
      if (review.status === Types.ReviewStatus.REJECTED) {
        return review.rejectedReason
          ? `Supervisor ${review.reviewer.name} rejected this record with the following comments:`
          : `Supervisor ${review.reviewer.name} rejected this record.`;
      } else {
        return review.rejectedReason
          ? `Supervisor ${review.reviewer.name} previously reviewed this record with the following comments:`
          : `This record was edited after it was reviewed by ${review.reviewer.name} and it needs to be reviewed again.`;
      }
    }
  }


  return (
    <>
      <Alert 
        severity='error' 
        className={classes.root} 
        data-testid='record-rejection-alert' 
        {...rest}
      >
        <Typography className={classes.title}>
          {title()}
        </Typography>

        {review.rejectedReason &&
        <Typography className={classes.comments}>
          <i>{review.rejectedReason}</i>
        </Typography>}

        {user.role.isHandler &&
        <Typography className={classes.instructions}>
          Please update your record based on any feedback provided and re-save to remove the Rejected status. 
          Your supervisor will review the record again to ensure the changes are satisfactory.
        </Typography>}
      </Alert>
    </>
  );
}